<script setup lang="ts">
import type { operations } from "#shopware";

// Constants
const defaultLimit = 20;
const defaultPage = 1;

// Refs and Reactive Variables
const route = useRoute();
const router = useRouter();
const limitQuery = route.query.limit ? Number(route.query.limit) : defaultLimit;
const pageQuery = route.query.p ? Number(route.query.p) : defaultPage;
const orderQuery = route.query.order ? (route.query.order as string) : undefined;
const { currentCategory } = useShopwareCategories();
const isOpen = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const selectedSearchFilters: {
	[key: string]: any;
} = ref<{
	allergen: Set<any>;
	manufacturer: Set<any>;
	properties: Set<any>;
	"min-price": number | undefined;
	"max-price": number | undefined;
	rating: number | undefined;
	"shipping-free": boolean | undefined;
}>({
	allergen: new Set(),
	manufacturer: new Set(),
	properties: new Set(),
	"min-price": undefined,
	"max-price": undefined,
	rating: undefined,
	"shipping-free": undefined
});

// Composables
const {
	filtersToQuery,
	getAvailableFilters,
	changeCurrentSortingOrder,
	getCurrentSortingOrder,
	loading,
	resetFilters,
	search,
	getSortingOrders
} = useListing({
	listingType: "categoryListing",
	categoryId: currentCategory.value?.id,
	defaultSearchCriteria: {
		limit: limitQuery,
		page: pageQuery,
		order: orderQuery,
		// reduceAggregations: true,
		associations: {
			crossSellings: {
				limit: 1,
				filter: [
					{
						type: "equals",
						field: "active",
						value: true
					}
				]
			}
		},
		includes: {
			product: [
				"id",
				"name",
				"translated",
				"cover",
				"customFields",
				"seoUrls",
				"calculatedPrice",
				"crossSellings"
			],
			product_cross_selling: ["id", "name", "translated.name"]
		}
	}
});

// Computed Properties
const selectedOptionIds = computed(() => [
	...selectedSearchFilters.value.allergen,
	...selectedSearchFilters.value.properties,
	...selectedSearchFilters.value.manufacturer
]);

const showResetFiltersButton = computed<boolean>(() => {
	if (
		selectedOptionIds.value.length !== 0 ||
		selectedSearchFilters.value["max-price"] ||
		selectedSearchFilters.value["min-price"] ||
		selectedSearchFilters.value["rating"] ||
		selectedSearchFilters.value["shipping-free"]
	) {
		return true;
	}

	return false;
});

const filtersCount = computed(() => selectedOptionIds.value.length);

const getAvailableFiltersWithOptions = computed(() => {
	return getAvailableFilters.value.filter((filter: any) => {
		if ("entities" in filter && Array.isArray(filter.entities)) {
			return filter.entities.length > 1;
		}

		if ("options" in filter && Array.isArray(filter.options)) {
			return filter.options.length > 1;
		}

		return false;
	});
});

const getSortingOrdersForRadio = computed(() => {
	return getSortingOrders.value?.map((sort: any) => ({
		label: sort.label,
		value: sort.key
	}));
});

const searchCriteriaForRequest = computed(
	() =>
		({
			// turn Set to array and then into string with | separator
			allergen: [...selectedSearchFilters.value.allergen]?.join("|"),
			// turn Set to array and then into string with | separator
			manufacturer: [...selectedSearchFilters.value.manufacturer]?.join("|"),
			// turn Set to array and then into string with | separator
			properties: [...selectedSearchFilters.value.properties]?.join("|"),
			"min-price": selectedSearchFilters.value["min-price"],
			"max-price": selectedSearchFilters.value["max-price"],
			order: getCurrentSortingOrder.value,
			"shipping-free": selectedSearchFilters.value["shipping-free"],
			rating: selectedSearchFilters.value["rating"],
			search: "",
			limit: defaultLimit
		}) as operations["searchPage post /search"]["body"]
);

// Methods
const onOptionSelectToggle = async ({ code, value }: { code: string; value: string }) => {
	if (!["allergen", "properties", "manufacturer"].includes(code)) {
		selectedSearchFilters.value[code] = value;
	} else {
		if (selectedSearchFilters.value[code]?.has(value)) {
			selectedSearchFilters.value[code]?.delete(value);
		} else {
			selectedSearchFilters.value[code]?.add(value);
		}
	}
};

const invokeClearFilters = async () => {
	isLoading.value = true;

	// remove filters from query and only keep search and order
	await router.push({
		query: {
			search: route.query.search,
			order: getCurrentSortingOrder.value
		}
	});

	// reset reactive filters and re-search products
	await resetFilters();

	selectedSearchFilters.value.allergen.clear();
	selectedSearchFilters.value.manufacturer.clear();
	selectedSearchFilters.value.properties.clear();
	selectedSearchFilters.value["min-price"] = undefined;
	selectedSearchFilters.value["max-price"] = undefined;
	selectedSearchFilters.value["rating"] = undefined;
	selectedSearchFilters.value["shipping-free"] = undefined;

	isLoading.value = false;
	isOpen.value = false;
};

const invokeSearchFilters = async () => {
	isLoading.value = true;

	await router.push({
		query: {
			search: route.query.search,
			...filtersToQuery(searchCriteriaForRequest.value)
		}
	});
	await search({
		...searchCriteriaForRequest.value,
		...route.query
	});

	isLoading.value = false;
	isOpen.value = false;
};

const invokeSortingOrder = async (order: string) => {
	isLoading.value = true;

	await router.push({
		query: {
			...filtersToQuery(searchCriteriaForRequest.value),
			order
		}
	});

	await changeCurrentSortingOrder(order, {
		...searchCriteriaForRequest.value,
		...route.query
	});

	isLoading.value = false;
	isOpen.value = false;
};

// Lifecycle Hooks
onMounted(() => {
	// Initialization logic here
	// set the selected filters from the url
	for (const param in route.query) {
		if (Object.prototype.hasOwnProperty.call(selectedSearchFilters.value, param)) {
			if (selectedSearchFilters.value[param] && typeof selectedSearchFilters.value[param] === "object") {
				(route.query[param] as unknown as string).split("|").forEach((element) => {
					selectedSearchFilters.value[param].add(element);
				});
			} else {
				selectedSearchFilters.value[param] = route.query[param];
			}
		}
	}
});
</script>

<template>
	<div>
		<KippieButton
			v-if="getAvailableFiltersWithOptions.length"
			pill
			size="small"
			color="black"
			class="!fixed lg:!relative z-10 bottom-6 lg:bottom-0 left-1/2 -translate-x-1/2 lg:translate-x-0 lg:left-0"
			@click="isOpen = !isOpen"
		>
			Filter en sorteer
			<template #right v-if="!filtersCount">
				<SvgIcon name="filter-icon" />
			</template>
			<template #right v-else> ({{ filtersCount }}) </template>
		</KippieButton>
		<ClientOnly>
			<KippieSidebar v-model="isOpen" placement="right" color="white">
				<template #top>
					<Typography variant="h6">Filter en sorteer</Typography>
				</template>
				<div class="flex flex-col gap-6">
					<Divider />

					<div class="flex flex-col gap-4" v-if="getSortingOrdersForRadio && getSortingOrdersForRadio.length">
						<span class="font-medium">Sorteren op:</span>

						<KippieRadioGroup
							name="Sorteer op"
							:values="getSortingOrdersForRadio"
							:initial-value="getCurrentSortingOrder"
							:disabled="isLoading"
							:class="{ 'pointer-events-none': isLoading }"
							@update="invokeSortingOrder"
						/>
					</div>
					<div v-for="filter in getAvailableFiltersWithOptions" :key="`${filter?.id || filter?.code}`">
						<AssortmentFilter
							:selected-option-ids="selectedOptionIds"
							:filter="filter"
							@select-filter-value="onOptionSelectToggle"
						/>
					</div>
				</div>
				<template #bottom>
					<div class="bg-sand flex gap-2 p-4 -m-5">
						<KippieButton
							block
							color="white"
							@click="invokeClearFilters"
							:disabled="loading || !showResetFiltersButton"
						>
							Wis ({{ filtersCount }})
						</KippieButton>
						<KippieButton block :disabled="loading" @click="invokeSearchFilters">
							Pas toe ({{ filtersCount }})
						</KippieButton>
					</div>
				</template>
			</KippieSidebar>
		</ClientOnly>
	</div>
</template>